import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Avatar,
    Chip,
    Link,
    Button,
    Collapse,
    Grid,
    Tooltip
} from "@mui/material";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StarIcon from "@mui/icons-material/Star";
import { academicGenealogy } from "../../info/genealogy";

export default function AcademicGenealogy() {
    const [expanded, setExpanded] = useState(false);
    const [imgErrors, setImgErrors] = useState({});

    const handleImgError = (name) => {
        setImgErrors((prev) => ({ ...prev, [name]: true }));
    };

    const getInitials = (name) => {
        return name
            .split(" ")
            .filter((n) => n.length > 0)
            .map((n) => n[0])
            .slice(0, 2)
            .join("");
    };

    // Quick preview avatars when collapsed
    const previewScholars = academicGenealogy.filter((_, idx) =>
        [0, 1, 2, 3, 4, 5, 10, 13, 14, 17, 20].includes(idx)
    );

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: "16px",
                border: "1px solid",
                borderColor: (theme) =>
                    theme.palette.mode === "dark"
                        ? "rgba(59, 130, 246, 0.25)"
                        : "rgba(59, 130, 246, 0.2)",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "rgba(30, 41, 59, 0.5)"
                        : "rgba(255, 255, 255, 0.85)",
                overflow: "hidden",
                transition: "border-color 0.2s ease"
            }}
        >
            {/* Header bar / Click to expand */}
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                    userSelect: "none"
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <HistoryEduIcon sx={{ color: "#3b82f6", fontSize: "1.8rem" }} />
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "text.primary" }}>
                                Academic Lineage (1506 – 2021)
                            </Typography>
                            <Chip
                                label="21 Generations"
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    bgcolor: "rgba(59, 130, 246, 0.12)",
                                    color: "#3b82f6"
                                }}
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Tracing doctoral mentorship back through historical figures in mathematics & science
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {!expanded && (
                        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
                            {previewScholars.map((s, idx) => (
                                <Tooltip title={`${s.name} (${s.year})`} key={s.name}>
                                    <Avatar
                                        src={!imgErrors[s.name] ? s.img : undefined}
                                        onError={() => handleImgError(s.name)}
                                        imgProps={{ referrerPolicy: "no-referrer" }}
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            fontSize: "0.65rem",
                                            fontWeight: 700,
                                            ml: idx === 0 ? 0 : -1,
                                            border: "2px solid",
                                            borderColor: (theme) =>
                                                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
                                            bgcolor: "#3b82f6",
                                            color: "#fff"
                                        }}
                                    >
                                        {getInitials(s.name)}
                                    </Avatar>
                                </Tooltip>
                            ))}
                        </Box>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{
                            borderRadius: "20px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            px: 1.8,
                            borderColor: "rgba(59, 130, 246, 0.4)",
                            color: "primary.main"
                        }}
                    >
                        {expanded ? "Hide" : "View Lineage"}
                    </Button>
                </Box>
            </Box>

            {/* Expanded Cards View: Picture + Name + Year + Main Field */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        pt: 1,
                        borderTop: "1px solid",
                        borderColor: "divider"
                    }}
                >
                    <Grid container spacing={2}>
                        {academicGenealogy.map((scholar, idx) => (
                            <Grid item xs={12} sm={6} md={6} lg={4} key={scholar.name}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        borderRadius: "14px",
                                        border: "1px solid",
                                        borderColor: idx === 0 ? "primary.main" : "divider",
                                        background: (theme) =>
                                            idx === 0
                                                ? theme.palette.mode === "dark"
                                                    ? "rgba(59, 130, 246, 0.12)"
                                                    : "rgba(238, 242, 255, 0.7)"
                                                : theme.palette.mode === "dark"
                                                ? "rgba(15, 23, 42, 0.5)"
                                                : "rgba(248, 250, 252, 0.8)",
                                        transition: "transform 0.15s ease, border-color 0.15s ease",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            transform: "translateY(-2px)"
                                        }
                                    }}
                                >
                                    {/* Prominent Picture */}
                                    <Avatar
                                        src={!imgErrors[scholar.name] ? scholar.img : undefined}
                                        onError={() => handleImgError(scholar.name)}
                                        imgProps={{ referrerPolicy: "no-referrer" }}
                                        variant="rounded"
                                        sx={{
                                            width: { xs: 64, sm: 70 },
                                            height: { xs: 64, sm: 70 },
                                            borderRadius: "12px",
                                            border: "1.5px solid",
                                            borderColor: "divider",
                                            bgcolor: (theme) =>
                                                theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                                            color: "text.primary",
                                            fontWeight: 700,
                                            fontSize: "1.2rem",
                                            flexShrink: 0
                                        }}
                                    >
                                        {getInitials(scholar.name)}
                                    </Avatar>

                                    {/* Name, Year & Main Field */}
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, flexWrap: "wrap" }}>
                                            <Typography variant="caption" fontWeight={700} sx={{ color: "primary.main" }}>
                                                {scholar.year} &middot; {scholar.school}
                                            </Typography>
                                            {scholar.genealogyId && (
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: "0.68rem",
                                                        color: "text.secondary",
                                                        opacity: 0.8
                                                    }}
                                                >
                                                    #{scholar.genealogyId}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            sx={{
                                                fontSize: "0.95rem",
                                                lineHeight: 1.3,
                                                mt: 0.2,
                                                color: "text.primary",
                                                wordBreak: "break-word",
                                                overflowWrap: "break-word",
                                                whiteSpace: "normal"
                                            }}
                                        >
                                            {scholar.url ? (
                                                <Link
                                                    href={scholar.url}
                                                    target="_blank"
                                                    rel="noopener"
                                                    underline="hover"
                                                    sx={{
                                                        color: "inherit",
                                                        wordBreak: "break-word",
                                                        "&:hover": { color: "primary.main" }
                                                    }}
                                                >
                                                    {scholar.name}
                                                    <OpenInNewIcon sx={{ fontSize: "0.75rem", opacity: 0.6, ml: 0.4, verticalAlign: "middle" }} />
                                                </Link>
                                            ) : (
                                                scholar.name
                                            )}
                                            {scholar.starred && (
                                                <StarIcon
                                                    sx={{
                                                        color: "#eab308",
                                                        fontSize: "1.05rem",
                                                        ml: 0.5,
                                                        verticalAlign: "-2px"
                                                    }}
                                                />
                                            )}
                                        </Typography>

                                        {/* Main Field */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.4,
                                                fontSize: "0.8rem",
                                                lineHeight: 1.35,
                                                color: "text.secondary",
                                                fontWeight: 500,
                                                wordBreak: "break-word"
                                            }}
                                        >
                                            {scholar.field}
                                        </Typography>

                                        {/* Extra Link if present */}
                                        {scholar.extraLink && (
                                            <Box sx={{ mt: 0.6 }}>
                                                <Link
                                                    href={scholar.extraLink.url}
                                                    target="_blank"
                                                    rel="noopener"
                                                    underline="hover"
                                                    sx={{
                                                        fontSize: "0.76rem",
                                                        fontWeight: 600,
                                                        color: "#3b82f6",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.4,
                                                        wordBreak: "break-word"
                                                    }}
                                                >
                                                    {scholar.extraLink.text}
                                                    <OpenInNewIcon sx={{ fontSize: "0.72rem", opacity: 0.8 }} />
                                                </Link>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Collapse>
        </Paper>
    );
}
